import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  OutlinedInput,
  FormControlLabel,
  RadioGroup,
  Radio,
  Switch,
  IconButton,
  Alert,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Divider,
  InputAdornment,
  Tooltip,
  CircularProgress,
  ClickAwayListener,
  Fade,
  Grow,
  Popper,
  List,
  ListItem,
  ListItemButton,
  Checkbox,
  ListItemIcon,
  ListItemText,
  Avatar,
  Skeleton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
  useTheme,
} from '@mui/material';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  CloudUpload as UploadIcon,
  Link as LinkIcon,
  PhotoCamera as CameraIcon,
  Visibility as PreviewIcon,
  Save as SaveIcon,
  ArrowBack as BackIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Category as CategoryIcon,
  CheckCircle as CheckCircleIcon,
  Work as WorkIcon,
  AttachMoney as MoneyIcon,
  Schedule as ScheduleIcon,
  LocationOn as LocationIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import toast from 'react-hot-toast';
import { motion } from 'framer-motion';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ProductService, CreateProductData } from '../services/productService';
import { CompanyService } from '../services/companyService';
import { FilterSettingsService } from '../services/filterSettingsService';
import { supabase } from '../lib/supabase';
import { Company } from '../types';
import MapSearchNew from '../components/MapSearch/MapSearchNew';
import { JOB_SECONDARY_LINKS } from '../components/Layout/JobSecondaryNavBar';
import { jobMegaMenuData } from '../components/Layout/jobMegaMenuData';
import ListingPromotionSelector, { ListingPromotionOption } from '../components/ListingPromotion/ListingPromotionSelector';
import { SubPayOptionsService } from '../services/subPayOptionsService';
import { PlatformSettingsService } from '../services/platformSettingsService';
import { PayPalService } from '../services/paypalService';
import { PlatformPurchasesService } from '../services/platformPurchasesService';

interface JobFormData {
  title: string;
  description: string;
  industries: string[];
  company: string;
  companyId: string; // Store the company ID instead of just name
  salary: number;
  salaryType: 'hourly' | 'fixed' | 'negotiable';
  location: string;
  remote: boolean;
  experience: string;
  jobType: 'full-time' | 'part-time' | 'contract' | 'freelance' | 'internship' | 'temporary' | 'volunteer';
  duration: string;
  isActive: boolean;
  images: string[];
  contactEmail?: string;
  applicationDeadline?: string;
  startDate?: string;
  // Additional job-specific fields
  experienceLevel?: 'entry-level' | 'mid-level' | 'senior-level' | 'executive';
  remoteWorkAllowed?: boolean;
  requiredSkills: string[];
  jobRequirements: string[];
  benefitsPerks: string[];
  expectedStartDate?: string;
  // Location coordinates
  latitude?: number;
  longitude?: number;
  locationName?: string;
  hasSpecificLocation?: boolean;
  // Detailed address components
  country?: string;
  state?: string;
  city?: string;
  district?: string;
  street?: string;
  houseNumber?: string;
  postalCode?: string;
  promotionAddons: string[];
}


// Experience levels
const experienceLevels = [
  'Entry Level (0-2 years)',
  'Mid Level (3-5 years)',
  'Senior Level (6-10 years)',
  'Expert Level (10+ years)'
];

// Job types
const jobTypes = [
  { value: 'full-time', label: 'Full Time' },
  { value: 'part-time', label: 'Part Time' },
  { value: 'contract', label: 'Contract' },
  { value: 'freelance', label: 'Freelance' }
];

// Salary types
const salaryTypes = [
  { value: 'hourly', label: 'Per Hour' },
  { value: 'fixed', label: 'Fixed Price' },
  { value: 'negotiable', label: 'Negotiable' }
];

// Sample job data for generating sample job postings
const sampleJobData = {
  titles: [
    'Senior React Developer',
    'Full Stack Engineer',
    'UI/UX Designer',
    'Product Manager',
    'Data Scientist',
    'DevOps Engineer',
    'Mobile App Developer',
    'Backend Developer',
    'Frontend Developer',
    'Software Architect',
    'QA Engineer',
    'Technical Writer',
    'Digital Marketing Specialist',
    'Content Creator',
    'Social Media Manager',
    'Graphic Designer',
    'Video Editor',
    'Project Manager',
    'Business Analyst',
    'Sales Representative',
    'Customer Success Manager',
    'HR Specialist',
    'Financial Analyst',
    'Operations Manager',
    'Marketing Coordinator'
  ],
  descriptions: [
    'We are looking for a talented and experienced professional to join our dynamic team. You will work on exciting projects, collaborate with cross-functional teams, and have the opportunity to make a real impact on our products and services.',
    'Join our innovative company and help us build cutting-edge solutions. This role offers excellent growth opportunities, competitive compensation, and a flexible work environment.',
    'We are seeking a passionate individual who is eager to learn and grow in a fast-paced environment. You will work with modern technologies and have access to professional development opportunities.',
    'This is an exciting opportunity to work with a talented team on challenging projects. We offer a collaborative work environment, competitive benefits, and opportunities for career advancement.',
    'We are looking for someone who is creative, detail-oriented, and passionate about delivering high-quality work. You will have the opportunity to work on diverse projects and grow your skills.',
    'Join our team and help us create amazing user experiences. This role offers the chance to work with cutting-edge technologies and collaborate with talented professionals.',
    'We are seeking a motivated individual who is ready to take on new challenges. You will work in a supportive environment with opportunities for learning and professional growth.',
    'This position offers the opportunity to work on innovative projects and make a meaningful impact. We provide a collaborative work environment and excellent career development opportunities.'
  ],
  skills: [
    'JavaScript', 'React', 'Node.js', 'Python', 'TypeScript', 'HTML', 'CSS', 'SQL', 'Git', 'Docker',
    'AWS', 'Azure', 'MongoDB', 'PostgreSQL', 'Redis', 'GraphQL', 'REST API', 'Agile', 'Scrum',
    'Figma', 'Adobe Creative Suite', 'Photoshop', 'Illustrator', 'Sketch', 'InVision', 'Framer',
    'Machine Learning', 'Data Analysis', 'Statistics', 'R', 'TensorFlow', 'PyTorch', 'Pandas',
    'Kubernetes', 'Jenkins', 'CI/CD', 'Linux', 'Bash', 'PowerShell', 'Terraform', 'Ansible',
    'iOS Development', 'Android Development', 'React Native', 'Flutter', 'Swift', 'Kotlin',
    'Java', 'C#', 'PHP', 'Ruby', 'Go', 'Rust', 'Vue.js', 'Angular', 'Svelte', 'Next.js'
  ],
  requirements: [
    'Bachelor\'s degree in Computer Science or related field',
    '3+ years of relevant work experience',
    'Strong problem-solving skills',
    'Excellent communication skills',
    'Ability to work independently and in a team',
    'Experience with modern development tools',
    'Knowledge of software development best practices',
    'Strong attention to detail',
    'Ability to meet deadlines',
    'Experience with version control systems',
    'Understanding of database design principles',
    'Experience with testing frameworks',
    'Knowledge of security best practices',
    'Experience with cloud platforms',
    'Strong analytical skills',
    'Creative thinking and innovation',
    'Project management experience',
    'Client-facing experience',
    'Mentoring and leadership skills',
    'Continuous learning mindset'
  ],
  benefits: [
    'Competitive salary',
    'Health insurance',
    'Dental insurance',
    'Vision insurance',
    '401(k) matching',
    'Flexible work hours',
    'Remote work options',
    'Professional development budget',
    'Paid time off',
    'Sick leave',
    'Maternity/Paternity leave',
    'Stock options',
    'Performance bonuses',
    'Gym membership',
    'Free meals',
    'Transportation allowance',
    'Learning opportunities',
    'Career advancement',
    'Team building activities',
    'Work-life balance',
    'Modern office space',
    'Latest technology tools',
    'Mentorship programs',
    'Conference attendance',
    'Certification support'
  ],
  locations: [
    'New York, NY',
    'San Francisco, CA',
    'Los Angeles, CA',
    'Chicago, IL',
    'Boston, MA',
    'Seattle, WA',
    'Austin, TX',
    'Denver, CO',
    'Remote',
    'Hybrid',
    'London, UK',
    'Toronto, Canada',
    'Berlin, Germany',
    'Amsterdam, Netherlands',
    'Sydney, Australia'
  ],
  durations: [
    '3 months',
    '6 months',
    '1 year',
    '2 years',
    'Ongoing',
    'Project-based',
    'Part-time',
    'Contract to hire'
  ]
};

const JobUploadPage: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isCompanyMode = searchParams.get('company') === 'true';
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [paypalProcessing, setPaypalProcessing] = useState(false);
  
  // Image upload state
  const [imageInput, setImageInput] = useState('');
  const [fileInputRef, setFileInputRef] = useState<HTMLInputElement | null>(null);

  // Map location state
  const [mapSearchOpen, setMapSearchOpen] = useState(false);
  const [selectedMapLocations, setSelectedMapLocations] = useState<string[]>([]);

  // Job industries (same source as JobSecondaryNavBar)
  const [industriesDialogOpen, setIndustriesDialogOpen] = useState(false);
  const [availableIndustries, setAvailableIndustries] = useState<string[]>(() => JOB_SECONDARY_LINKS.map((l) => l.label));
  const [industriesEnabled, setIndustriesEnabled] = useState<Record<string, boolean>>({});
  const [subcategoriesEnabled, setSubcategoriesEnabled] = useState<Record<string, Record<string, boolean>>>({});
  const [activeIndustryInDialog, setActiveIndustryInDialog] = useState<string | null>(null);

  // Companies state
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loadingCompanies, setLoadingCompanies] = useState(true);
  const [companiesError, setCompaniesError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<JobFormData>({
    defaultValues: {
      title: '',
      description: '',
      industries: [],
      company: '',
      companyId: '',
      salary: 0,
      salaryType: 'hourly',
      location: '',
      remote: false,
      experience: '',
      jobType: 'full-time',
        duration: '',
        isActive: true,
      images: [],
      contactEmail: '',
      applicationDeadline: '',
      startDate: '',
      // Additional job-specific fields
      experienceLevel: 'entry-level',
      remoteWorkAllowed: false,
      requiredSkills: [],
      jobRequirements: [],
      benefitsPerks: [],
      expectedStartDate: '',
      // Location coordinates
      latitude: undefined,
      longitude: undefined,
      locationName: '',
      hasSpecificLocation: false,
      // Detailed address components
      country: '',
      state: '',
      city: '',
      district: '',
      street: '',
      houseNumber: '',
      postalCode: '',
      promotionAddons: [],
    },
  });

  const watchedImages = watch('images');
  const watchedIndustries = watch('industries');

  useEffect(() => {
    const loadEnabledIndustries = async () => {
      try {
        const res = await fetch('/website-settings.json', { cache: 'no-store' });
        if (!res.ok) return;
        const json = await res.json();
        const enabled: Record<string, boolean> = json?.categoriesEnabled || {};
        setIndustriesEnabled(enabled);
        setSubcategoriesEnabled(json?.subcategoriesEnabled || {});
      } catch {
        // ignore
      }
    };

    loadEnabledIndustries();
  }, []);

  useEffect(() => {
    const all = JOB_SECONDARY_LINKS.map((l) => l.label);
    const filtered = all.filter((label) => {
      if (Object.prototype.hasOwnProperty.call(industriesEnabled, label)) {
        return !!industriesEnabled[label];
      }
      return true;
    });
    setAvailableIndustries(filtered);

    setActiveIndustryInDialog((prev) => {
      if (prev && filtered.includes(prev)) return prev;
      return filtered.length > 0 ? filtered[0] : null;
    });
  }, [industriesEnabled]);

  const isSubEnabled = (top: string, label: string): boolean => {
    const m = subcategoriesEnabled?.[top];
    if (!m || typeof m[label] === 'undefined') return true;
    return !!m[label];
  };

  const getSubcategoryPathsForIndustry = (top: string): string[] => {
    const content: any = (jobMegaMenuData as any)[top] || (jobMegaMenuData as any).default;
    const left: Array<{ key: string; label: string }> = Array.isArray(content?.left) ? content.left : [];
    const rightByKey: Record<string, any[]> = content?.rightByKey || {};

    const paths: string[] = [];
    for (const leftItem of left) {
      const groupLabel = String(leftItem?.label || '').trim();
      const isAllGroup = leftItem?.key === 'all' || groupLabel.toLowerCase() === 'alle anzeigen' || groupLabel.toLowerCase() === 'all roles';
      const cols: any[] = Array.isArray(rightByKey[leftItem.key]) ? rightByKey[leftItem.key] : [];
      for (const col of cols) {
        const items: any[] = Array.isArray(col?.items) ? col.items : [];
        for (const it of items) {
          const lbl = typeof it === 'string' ? it : String(it?.label || '').trim();
          if (!lbl) continue;
          if (lbl.toLowerCase() === 'alle anzeigen' || lbl.toLowerCase() === 'all roles') continue;
          if (!isSubEnabled(top, lbl)) continue;
          const path = isAllGroup ? `${top} / ${lbl}` : `${top} / ${groupLabel} / ${lbl}`;
          paths.push(path);
        }
      }
    }

    return Array.from(new Set(paths));
  };

  // Function to generate sample job data
  const generateSampleJob = () => {
    if (availableIndustries.length === 0 || companies.length === 0) {
      toast.error('Please wait for industries and companies to load');
      return;
    }

    // Randomly select from available data
    const randomTitle = sampleJobData.titles[Math.floor(Math.random() * sampleJobData.titles.length)];
    const randomDescription = sampleJobData.descriptions[Math.floor(Math.random() * sampleJobData.descriptions.length)];
    const randomIndustry = availableIndustries[Math.floor(Math.random() * availableIndustries.length)];
    const possiblePaths = getSubcategoryPathsForIndustry(randomIndustry);
    const randomIndustryPath = possiblePaths.length > 0
      ? possiblePaths[Math.floor(Math.random() * possiblePaths.length)]
      : randomIndustry;
    const randomCompany = companies[Math.floor(Math.random() * companies.length)];
    const randomLocation = sampleJobData.locations[Math.floor(Math.random() * sampleJobData.locations.length)];
    const randomDuration = sampleJobData.durations[Math.floor(Math.random() * sampleJobData.durations.length)];
    const randomJobType = jobTypes[Math.floor(Math.random() * jobTypes.length)];
    const randomSalaryType = salaryTypes[Math.floor(Math.random() * salaryTypes.length)];
    const randomExperience = experienceLevels[Math.floor(Math.random() * experienceLevels.length)];

    // Generate random salary based on job type and experience
    const salaryRanges = {
      'entry-level': { min: 15, max: 35 },
      'mid-level': { min: 35, max: 75 },
      'senior-level': { min: 75, max: 150 },
      'executive': { min: 150, max: 300 }
    };
    
    const experienceLevel = randomExperience.toLowerCase().includes('entry') ? 'entry-level' :
                          randomExperience.toLowerCase().includes('mid') ? 'mid-level' :
                          randomExperience.toLowerCase().includes('senior') ? 'senior-level' : 'executive';
    
    const range = salaryRanges[experienceLevel as keyof typeof salaryRanges];
    const randomSalary = Math.floor(Math.random() * (range.max - range.min + 1)) + range.min;

    // Generate random skills (3-8 skills)
    const numSkills = Math.floor(Math.random() * 6) + 3;
    const randomSkills = sampleJobData.skills
      .sort(() => 0.5 - Math.random())
      .slice(0, numSkills);

    // Generate random requirements (3-6 requirements)
    const numRequirements = Math.floor(Math.random() * 4) + 3;
    const randomRequirements = sampleJobData.requirements
      .sort(() => 0.5 - Math.random())
      .slice(0, numRequirements);

    // Generate random benefits (4-8 benefits)
    const numBenefits = Math.floor(Math.random() * 5) + 4;
    const randomBenefits = sampleJobData.benefits
      .sort(() => 0.5 - Math.random())
      .slice(0, numBenefits);

    // Generate future dates
    const futureDate = new Date();
    futureDate.setDate(futureDate.getDate() + Math.floor(Math.random() * 30) + 7); // 1-5 weeks from now
    const applicationDeadline = futureDate.toISOString().split('T')[0];

    const startDate = new Date();
    startDate.setDate(startDate.getDate() + Math.floor(Math.random() * 60) + 14); // 2-10 weeks from now
    const expectedStartDate = startDate.toISOString().split('T')[0];

    // Set all form values
    setValue('title', randomTitle);
    setValue('description', randomDescription);
    setValue('industries', [randomIndustryPath]);
    setValue('company', randomCompany.name);
    setValue('companyId', randomCompany.id);
    setValue('salary', randomSalary);
    setValue('salaryType', randomSalaryType.value as 'hourly' | 'fixed' | 'negotiable');
    setValue('location', randomLocation);
    setValue('jobType', randomJobType.value as 'full-time' | 'part-time' | 'contract' | 'freelance' | 'internship' | 'temporary' | 'volunteer');
    setValue('experience', randomExperience);
    setValue('duration', randomDuration);
    setValue('requiredSkills', randomSkills);
    setValue('jobRequirements', randomRequirements);
    setValue('benefitsPerks', randomBenefits);
    setValue('contactEmail', `hr@${randomCompany.name.toLowerCase().replace(/\s+/g, '')}.com`);
    setValue('applicationDeadline', applicationDeadline);
    setValue('startDate', expectedStartDate);
    setValue('remote', randomLocation.toLowerCase().includes('remote') || randomLocation.toLowerCase().includes('hybrid'));
    setValue('isActive', true);

    toast.success('Sample job data generated! Review and modify as needed.');
  };

  // Load user companies
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoadingCompanies(true);
        setCompaniesError(null);

        // Load user's companies
        const { data: { user }, error: authError } = await supabase.auth.getUser();
        if (authError || !user) {
          throw new Error('Please log in to create a job');
        }

        const userCompanies = await CompanyService.getUserCompanies(user.id);
        setCompanies(userCompanies);
        
      } catch (error) {
        console.error('Error loading data:', error);
        setCompaniesError('Failed to load companies');
      } finally {
        setLoadingCompanies(false);
      }
    };

    loadData();
  }, []);

  const steps = ['Basic Information', 'Job Details', 'Location', 'Requirements & Benefits', 'Boost & Visibility', 'Review & Publish'];

  const [promotionOptions, setPromotionOptions] = React.useState<ListingPromotionOption[]>([]);
  const [promotionOptionsLoading, setPromotionOptionsLoading] = React.useState(false);
  const [promotionOptionsError, setPromotionOptionsError] = React.useState<string | null>(null);
  const [promotionOptionsLoadedOnce, setPromotionOptionsLoadedOnce] = React.useState(false);
  const [resolvedPromotionOptions, setResolvedPromotionOptions] = React.useState<ReturnType<typeof SubPayOptionsService.resolveActive> | null>(null);

  const loadPromotionOptions = React.useCallback(async () => {
    setPromotionOptionsLoading(true);
    setPromotionOptionsError(null);
    try {
      const doc = await SubPayOptionsService.getOptions();
      const resolved = SubPayOptionsService.resolveActive(doc);
      const currency = resolved.currency || 'EUR';
      setResolvedPromotionOptions(resolved);
      const opts = (resolved.options || [])
        .filter((o) => o && o.enabled !== false)
        .map((o) => ({
          id: o.id,
          title: o.title,
          description: o.description,
          price: Number(o.price) || 0,
          currency,
          badge: o.badge,
          recommended: !!o.recommended,
        }));
      setPromotionOptions(opts);
      setPromotionOptionsLoadedOnce(true);
    } catch (e: any) {
      const msg = e?.message ? String(e.message) : 'Failed to load boost options';
      console.error('Failed to load boost options:', e);
      setPromotionOptions([]);
      setResolvedPromotionOptions(null);
      setPromotionOptionsError(msg);
      setPromotionOptionsLoadedOnce(true);
    } finally {
      setPromotionOptionsLoading(false);
    }
  }, []);

  // Load boost options when user enters the Boost & Visibility step (index 4)
  React.useEffect(() => {
    if (activeStep !== 4) return;
    if (promotionOptionsLoadedOnce) return;
    loadPromotionOptions();
  }, [activeStep, promotionOptionsLoadedOnce, loadPromotionOptions]);

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return 'Basic Information';
      case 1:
        return 'Job Details';
      case 2:
        return 'Requirements & Benefits';
      case 3:
        return 'Review & Publish';
      default:
        return 'Unknown step';
    }
  };

  const handlePublishClick = async (formData: JobFormData) => {
    if (loading || paypalProcessing) return;

    const selectedIds = Array.isArray(formData.promotionAddons) ? formData.promotionAddons : [];
    const currency = (resolvedPromotionOptions?.currency || 'EUR').trim() || 'EUR';
    const byId = new Map((promotionOptions || []).map((o) => [String(o.id), o] as const));
    const total = selectedIds.reduce((sum, id) => sum + (Number(byId.get(String(id))?.price) || 0), 0);

    if (total > 0) {
      let platformEmail = '';
      try {
        platformEmail = await PlatformSettingsService.getPlatformPayPalEmail();
      } catch (e: any) {
        // Don't block publish here: the edge function can resolve via service role.
        // Still log for debugging and proceed with an empty email.
        console.error('Failed to resolve platform PayPal email (will defer to edge function):', e);
        platformEmail = '';
      }

      try {
        await openPayPalForBoost({ amount: total, currency, platformEmail });
      } catch (e: any) {
        toast.error(e?.message || 'Payment failed');
        return;
      }
    }

    await onSubmit(formData);
  };

  const handlePublishInvalid = (errs: any) => {
    const fieldLabels: Record<string, string> = {
      title: 'Job title',
      description: 'Job description',
      industries: 'Categories',
      company: 'Company',
      companyId: 'Company',
      salary: 'Salary',
      jobType: 'Job type',
      location: 'Location',
    };
    const fields = Object.keys(errs || {});
    if (fields.length === 0) {
      toast.error('Please review the form fields and try again.');
      return;
    }
    const nice = fields.slice(0, 6).map((k) => fieldLabels[k] || k);
    const more = fields.length > nice.length ? ` (+${fields.length - nice.length} more)` : '';
    toast.error(`Please fix: ${nice.join(', ')}${more}`);
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleReset = () => {
    setActiveStep(0);
  };

  // Helper function to map experience string to enum value
  const mapExperienceToLevel = (experience: string): 'entry-level' | 'mid-level' | 'senior-level' | 'executive' => {
    if (experience.toLowerCase().includes('entry')) return 'entry-level';
    if (experience.toLowerCase().includes('mid')) return 'mid-level';
    if (experience.toLowerCase().includes('senior')) return 'senior-level';
    if (experience.toLowerCase().includes('executive')) return 'executive';
    return 'entry-level'; // default
  };

  // Validation function to check job data before submission
  const validateJobData = (data: JobFormData): string[] => {
    const errors: string[] = [];
    
    // Required fields validation
    if (!data.title || data.title.trim().length === 0) {
      errors.push('Job title is required');
    }
    
    if (!data.description || data.description.trim().length === 0) {
      errors.push('Job description is required');
    }
    
    if (!Array.isArray(data.industries) || data.industries.length === 0) {
      errors.push('At least one industry is required');
    }
    
    if (!data.company || data.company.trim().length === 0) {
      errors.push('Company is required');
    }
    
    if (!data.companyId || data.companyId.trim().length === 0) {
      errors.push('Company ID is required');
    }
    
    if (!data.salary || data.salary <= 0) {
      errors.push('Salary must be greater than 0');
    }
    
    if (!data.jobType || data.jobType.trim().length === 0) {
      errors.push('Job type is required');
    }
    
    // Location validation (only if hasSpecificLocation is true)
    if (data.hasSpecificLocation) {
      if (!data.location || data.location.trim().length === 0) {
        errors.push('Location is required when job has specific location');
      }
    }
    
    // Email validation
    if (data.contactEmail && data.contactEmail.trim().length > 0) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(data.contactEmail)) {
        errors.push('Invalid email format');
      }
    }
    
    // Date validation
    if (data.applicationDeadline) {
      const deadlineDate = new Date(data.applicationDeadline);
      const today = new Date();
      if (deadlineDate <= today) {
        errors.push('Application deadline must be in the future');
      }
    }
    
    if (data.expectedStartDate) {
      const startDate = new Date(data.expectedStartDate);
      const today = new Date();
      if (startDate <= today) {
        errors.push('Expected start date must be in the future');
      }
    }
    
    // Log validation details
    console.log('🔍 Validation check:', {
      data,
      errors,
      timestamp: new Date().toISOString()
    });
    
    return errors;
  };

  const openPayPalForBoost = React.useCallback(
    async (input: { amount: number; currency: string; platformEmail: string }): Promise<void> => {
      if (paypalProcessing) return;
      setPaypalProcessing(true);
      try {
        const result = await PayPalService.createPaymentOrder({
          vendorEmail: input.platformEmail,
          vendorAmount: input.amount,
          platformEmail: input.platformEmail,
          platformAmount: 0,
          currency: input.currency,
        });

        if (!result.success || !result.approvalUrl || !result.orderId) {
          throw new Error(result.error || 'Failed to create PayPal order');
        }

        const width = 600;
        const height = 700;
        const left = (window.screen.width - width) / 2;
        const top = (window.screen.height - height) / 2;

        const paypalWindow = window.open(
          result.approvalUrl,
          'PayPal Payment',
          `width=${width},height=${height},left=${left},top=${top},toolbar=no,menubar=no,scrollbars=yes,resizable=yes`
        );

        if (!paypalWindow) {
          throw new Error('Popup blocked. Please allow popups for this site.');
        }

        toast.success('Opening PayPal...');

        await new Promise<void>((resolve, reject) => {
          let done = false;

          const cleanup = () => {
            window.removeEventListener('message', onMessage);
            if (interval) window.clearInterval(interval);
            if (timeout) window.clearTimeout(timeout);
          };

          const succeed = () => {
            if (done) return;
            done = true;
            cleanup();
            resolve();
          };

          const fail = (err: any) => {
            if (done) return;
            done = true;
            cleanup();
            reject(err);
          };

          const checkStatus = async (): Promise<'completed' | 'cancelled' | 'pending'> => {
            const statusResult = await PayPalService.checkPaymentStatus(result.orderId as string);
            if (statusResult.success && statusResult.completed) {
              succeed();
              return 'completed';
            }
            if (statusResult.success && statusResult.cancelled) {
              fail(new Error('Payment was cancelled'));
              return 'cancelled';
            }
            return 'pending';
          };

          const waitForCompletion = async (opts?: { attempts?: number; delayMs?: number }): Promise<'completed' | 'cancelled' | 'pending'> => {
            const attempts = Math.max(1, opts?.attempts ?? 8);
            const delayMs = Math.max(250, opts?.delayMs ?? 900);
            for (let i = 0; i < attempts; i++) {
              const s = await checkStatus();
              if (s !== 'pending') return s;
              await new Promise((r) => window.setTimeout(r, delayMs));
            }
            return 'pending';
          };

          const onMessage = async (event: MessageEvent) => {
            if (event.origin !== window.location.origin) return;
            if (event.data?.type === 'PAYMENT_CANCELLED') {
              fail(new Error('Payment was cancelled'));
              return;
            }
            if (event.data?.type === 'PAYMENT_SUCCESS') {
              try {
                await waitForCompletion({ attempts: 10, delayMs: 750 });
              } catch (e) {
                fail(e);
              }
            }
          };

          window.addEventListener('message', onMessage);

          const interval = window.setInterval(async () => {
            try {
              if (paypalWindow.closed) {
                const s = await waitForCompletion({ attempts: 10, delayMs: 750 });
                if (s === 'completed') return;
                if (s === 'cancelled') return;
                fail(new Error('Payment window was closed'));
              }
            } catch {
              // ignore
            }
          }, 1000);

          const timeout = window.setTimeout(() => {
            try {
              if (!paypalWindow.closed) paypalWindow.close();
            } catch {
              // ignore
            }
            fail(new Error('Payment timed out. Please try again.'));
          }, 10 * 60 * 1000);
        });
      } finally {
        setPaypalProcessing(false);
      }
    },
    [paypalProcessing]
  );

  const onSubmit = async (data: JobFormData) => {
    setLoading(true);
    try {
      console.log('🔍 Form data received:', data);
      
      // Validate required fields before processing
      const validationErrors = validateJobData(data);
      if (validationErrors.length > 0) {
        console.error('❌ Validation errors:', validationErrors);
        toast.error(`Validation failed: ${validationErrors.join(', ')}`);
        setLoading(false);
        return;
      }

      // Check content filters before proceeding
      try {
        const filterChecks = await FilterSettingsService.checkMultipleContentViolations([
          { type: 'name', value: data.title },
          { type: 'description', value: data.description },
          { type: 'brand', value: data.company },
          { type: 'tags', value: (data.requiredSkills || []).join(' ') },
        ]);

        if (filterChecks.violated) {
          // Find blocking filters
          const blockingFilters = filterChecks.violations
            .flatMap(v => v.violations)
            .filter(f => f.action_type === 'block');

          if (blockingFilters.length > 0) {
            const blockedFields = filterChecks.violations
              .filter(v => v.violations.some(f => f.action_type === 'block'))
              .map(v => v.type);
            
            toast.error(
              `Content blocked: Your ${blockedFields.join(', ')} contains prohibited content. Please review and modify your content.`,
              { duration: 6000 }
            );
            setLoading(false);
            return;
          }

          // If only flagging filters matched, show warning but allow
          const flaggedFields = filterChecks.violations.map(v => v.type).join(', ');
          toast(
            `Content flagged for review: Your ${flaggedFields} may require admin review.`,
            { 
              duration: 5000,
              icon: '⚠️',
              style: {
                background: '#ff9800',
                color: '#fff',
              },
            }
          );
        }
      } catch (error) {
        console.error('Error checking content filters:', error);
        // Continue with upload if filter check fails (fail open)
      }
      
      // Convert job data to product format for database storage
      const selectedBoostIds = Array.isArray(data.promotionAddons) ? data.promotionAddons : [];
      const boostOptionById = new Map(
        (resolvedPromotionOptions?.options || []).map((o) => [String(o.id || ''), o] as const)
      );
      const boost = selectedBoostIds
        .filter((id) => !!String(id || '').trim())
        .map((id) => {
          const opt = boostOptionById.get(String(id));
          return {
            id: String(id),
            title: opt?.title,
            description: opt?.description,
            price: typeof opt?.price === 'number' ? opt?.price : undefined,
            currency: resolvedPromotionOptions?.currency,
            badge: opt?.badge,
            recommended: !!opt?.recommended,
            action: opt?.action,
          };
        });

      const productData: CreateProductData = {
        name: data.title,
        description: data.description,
        price: data.salary,
        original_price: undefined,
        categories: data.industries,
        company_id: data.companyId, // Store the company ID
        images: data.images || [],
        colors: [],
        custom_colors: [],
        size_system: 'job',
        sizes: [],
        condition: 'new' as const,
        brand: data.company,
        model: data.jobType,
        sku: data.salaryType,
        weight: undefined,
        dimensions: undefined,
        tags: [...(data.requiredSkills || []), ...(data.promotionAddons || []).map((x) => `boost:${x}`)],
        boost,
        delivery_option_ids: [],
        is_job: true,
        is_featured: Array.isArray(data.promotionAddons) ? data.promotionAddons.includes('featured') : false,
        // Job-specific fields
        salary_type: data.salaryType,
        location: data.location,
        job_type: data.jobType,
        experience_level: data.experienceLevel || mapExperienceToLevel(data.experience),
        duration: data.duration,
        remote_work_allowed: data.remote || data.remoteWorkAllowed,
        required_skills: data.requiredSkills || [],
        job_requirements: data.jobRequirements || [],
        benefits_perks: data.benefitsPerks || [],
        contact_email: data.contactEmail,
        application_deadline: data.applicationDeadline,
        expected_start_date: data.expectedStartDate || data.startDate,
        // Location coordinates
        latitude: data.latitude,
        longitude: data.longitude,
        location_name: data.locationName,
      };

      console.log('📊 Product data being sent:', productData);

      // Create job in database using ProductService
      const job = await ProductService.createProductDirect(productData);
      
      console.log('Job created:', job);

      try {
        const currency = (resolvedPromotionOptions?.currency || 'EUR').trim() || 'EUR';
        const items = boost;
        const totalAmount = (items || []).reduce((sum, it: any) => sum + (Number((it as any)?.price) || 0), 0);
        await PlatformPurchasesService.create({
          productId: (job as any)?.id || null,
          currency,
          totalAmount,
          items,
          status: 'succeeded',
          provider: 'paypal',
        });
      } catch (e) {
        console.warn('[JobUploadPage] Failed to write platform_purchases record (non-fatal):', e);
      }

      setShowSuccessModal(true);
    } catch (error) {
      console.error('❌ Error creating job:', error);
      
      // Enhanced error handling with detailed messages
      let errorMessage = 'Failed to create job posting';
      let errorDetails = '';
      
      if (error instanceof Error) {
        errorDetails = error.message;
        
        // Check for specific error types
        if (error.message.includes('duplicate key')) {
          errorMessage = 'Job posting with this title already exists';
          errorDetails = 'Please choose a different job title';
        } else if (error.message.includes('foreign key')) {
          errorMessage = 'Invalid company or category selected';
          errorDetails = 'Please select a valid company and category';
        } else if (error.message.includes('required')) {
          errorMessage = 'Missing required information';
          errorDetails = 'Please fill in all required fields';
        } else if (error.message.includes('permission')) {
          errorMessage = 'Permission denied';
          errorDetails = 'You do not have permission to create job postings';
        } else if (error.message.includes('network') || error.message.includes('fetch')) {
          errorMessage = 'Network error';
          errorDetails = 'Please check your internet connection and try again';
        } else if (error.message.includes('validation')) {
          errorMessage = 'Invalid data provided';
          errorDetails = 'Please check your input and try again';
        }
      }
      
      // Log detailed error information
      console.error('📋 Error Details:', {
        message: errorMessage,
        details: errorDetails,
        originalError: error,
        formData: data,
        timestamp: new Date().toISOString()
      });
      
      // Show detailed error message to user
      toast.error(`${errorMessage}. ${errorDetails}`);
      
      // Also show a more detailed error in console for debugging
      console.error('🔍 Full error context:', {
        error,
        formData: data,
        stack: error instanceof Error ? error.stack : 'No stack trace available'
      });
    } finally {
      setLoading(false);
    }
  };

  const renderBasicInformation = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" fontWeight="bold">
            Job Information
          </Typography>
          <Tooltip 
            title={
              loadingCompanies 
                ? "Loading companies..." 
                : availableIndustries.length === 0 || companies.length === 0
                ? "No industries or companies available"
                : "Fill all form fields with sample data to get started quickly"
            }
          >
            <Button
              variant="outlined"
              onClick={generateSampleJob}
              disabled={loadingCompanies || availableIndustries.length === 0 || companies.length === 0}
              startIcon={loadingCompanies ? <CircularProgress size={16} /> : <AddIcon />}
              sx={{
                borderColor: 'primary.main',
                color: 'primary.main',
                '&:hover': {
                  borderColor: 'primary.dark',
                  backgroundColor: 'action.hover',
                },
                '&:disabled': {
                  borderColor: '#e0e0e0',
                  color: '#9e9e9e',
                },
                textTransform: 'none',
                fontWeight: 500,
                px: 3,
                py: 1,
              }}
            >
              Generate Sample
            </Button>
          </Tooltip>
          
          <Tooltip title="Debug: Log current form data to console">
            <Button
              variant="outlined"
              onClick={() => {
                const formData = getValues();
                console.log('🔍 Current form data:', formData);
                console.log('🔍 Form errors:', errors);
                console.log('🔍 Validation errors:', validateJobData(formData));
                toast.success('Form data logged to console');
              }}
              sx={{
                borderColor: '#ff9800',
                color: '#ff9800',
                '&:hover': {
                  borderColor: '#f57c00',
                  backgroundColor: 'rgba(255, 152, 0, 0.04)',
                },
                textTransform: 'none',
                fontWeight: 500,
                px: 3,
                py: 1,
              }}
            >
              Debug Form
            </Button>
          </Tooltip>
        </Box>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Controller
            name="title"
            control={control}
            rules={{ required: 'Job title is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Job Title"
                fullWidth
                error={!!errors.title}
                helperText={errors.title?.message}
                placeholder="e.g., Senior React Developer"
              />
            )}
          />

          <Controller
            name="description"
            control={control}
            rules={{ required: 'Job description is required' }}
            render={({ field }) => (
              <TextField
                {...field}
                label="Job Description"
                fullWidth
                multiline
                rows={4}
                error={!!errors.description}
                helperText={errors.description?.message}
                placeholder="Describe the role, responsibilities, and what you're looking for..."
              />
            )}
          />

          <Box sx={{ mb: 3 }}>
            <Controller
              name="industries"
              control={control}
              rules={{ validate: (v) => (Array.isArray(v) && v.length > 0) || 'At least one industry is required' }}
              render={() => (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                    <Typography variant="subtitle1" fontWeight={700}>
                      Kategorien (Job Navigation)
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<CategoryIcon />}
                      onClick={() => setIndustriesDialogOpen(true)}
                      sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
                    >
                      Kategorien auswählen
                    </Button>
                  </Box>

                  <Box sx={{ mt: 1.5, display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {watchedIndustries && watchedIndustries.length > 0 ? (
                      watchedIndustries.map((label) => (
                        <Chip
                          key={label}
                          label={label}
                          onDelete={() => setValue('industries', (watchedIndustries || []).filter((x) => x !== label))}
                          sx={{ fontWeight: 700 }}
                        />
                      ))
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        Bitte wähle mindestens eine Kategorie.
                      </Typography>
                    )}
                  </Box>

                  {errors.industries && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.75, display: 'block' }}>
                      {String(errors.industries.message || 'At least one industry is required')}
                    </Typography>
                  )}
                </Box>
              )}
            />
          </Box>

          <Controller
            name="company"
            control={control}
            rules={{ required: 'Company selection is required' }}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.company}>
                <InputLabel>Select Company</InputLabel>
                <Select
                  {...field}
                  label="Select Company"
                  disabled={loadingCompanies}
                  error={!!errors.company}
                >
                  {loadingCompanies ? (
                    <MenuItem disabled>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <CircularProgress size={16} />
                        <Typography variant="body2">Loading companies...</Typography>
                      </Box>
                    </MenuItem>
                  ) : companiesError ? (
                    <MenuItem disabled>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Typography variant="body2" color="error">
                          {companiesError}
                        </Typography>
                      </Box>
                    </MenuItem>
                  ) : companies.length === 0 ? (
                    <MenuItem disabled>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%' }}>
                        <Typography variant="body2" color="text.secondary">
                          No companies found. Create a company first.
                        </Typography>
                      </Box>
                    </MenuItem>
                  ) : (
                    companies.map((company) => (
                      <MenuItem key={company.id} value={company.name}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
                          <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem' }}>
                            {company.name.charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography variant="body1" fontWeight="medium">
                            {company.name}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))
                  )}
                </Select>
                {errors.company && (
                  <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                    {errors.company.message}
                  </Typography>
                )}
              </FormControl>
            )}
          />
        </Box>
      </CardContent>
    </Card>
  );

  const renderBoostAndVisibility = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
      {promotionOptionsLoading ? (
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 3, display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress size={22} />
            <Box>
              <Typography variant="h6" fontWeight={900}>
                Loading boost options…
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Please wait a moment.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      ) : promotionOptionsError ? (
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={900}>
              Boost & Visibility
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              Couldn’t load boost options from the server.
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {promotionOptionsError}
            </Typography>
            <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="contained" onClick={loadPromotionOptions}>
                Retry
              </Button>
              <Button variant="outlined" onClick={() => setPromotionOptionsError(null)}>
                Dismiss
              </Button>
            </Box>
          </CardContent>
        </Card>
      ) : promotionOptions.length > 0 ? (
        <ListingPromotionSelector
          options={promotionOptions}
          value={watch('promotionAddons') || []}
          onChange={(next) => setValue('promotionAddons', next)}
          title="Boost visibility"
          subtitle="Optional add-ons to get more exposure. You can skip this step and publish normally."
        />
      ) : (
        <Card sx={{ borderRadius: 3 }}>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" fontWeight={900}>
              Boost & Visibility
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
              No boost options configured yet. An admin can add them in Admin Dashboard → Boost & Visibility.
            </Typography>
            <Box sx={{ mt: 2 }}>
              <Button variant="outlined" onClick={loadPromotionOptions}>
                Refresh
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}

      <Card sx={{ borderRadius: 3 }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="body2" color="text.secondary">
            These options only affect visibility. Payment processing/checkout can be connected later.
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );

  const renderIndustriesDialog = () => {
    const selected = new Set(watchedIndustries || []);
    const activeTop = activeIndustryInDialog;
    const subPaths = activeTop ? getSubcategoryPathsForIndustry(activeTop) : [];

    const getSelectedCountForTop = (top: string) => {
      const all = getSubcategoryPathsForIndustry(top);
      let cnt = 0;
      for (const p of all) if (selected.has(p)) cnt++;
      return { total: all.length, selected: cnt };
    };

    return (
      <Dialog
        open={industriesDialogOpen}
        onClose={() => setIndustriesDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ fontWeight: 900 }}>
          Kategorien auswählen
        </DialogTitle>
        <DialogContent dividers sx={{ p: 0 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1.25fr' }, minHeight: 320 }}>
            <Box sx={{ borderRight: { sm: '1px solid' }, borderColor: 'divider' }}>
              <List disablePadding>
                {availableIndustries.map((label) => {
                  const counts = getSelectedCountForTop(label);
                  const hasAny = counts.selected > 0;
                  const isAll = counts.total > 0 && counts.selected === counts.total;
                  const indeterminate = counts.selected > 0 && counts.selected < counts.total;
                  const isActive = activeIndustryInDialog === label;
                  return (
                    <ListItem key={label} disablePadding>
                      <ListItemButton
                        onClick={() => setActiveIndustryInDialog(label)}
                        sx={{ py: 1.1, px: 2, ...(isActive && { bgcolor: 'action.hover' }) }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Checkbox
                            edge="start"
                            checked={isAll || (counts.total === 0 && selected.has(label))}
                            indeterminate={indeterminate}
                            onClick={(e) => {
                              e.stopPropagation();
                              const next = new Set(selected);
                              const allPaths = getSubcategoryPathsForIndustry(label);

                              // If there are subcategories, toggle all; otherwise toggle top-level only
                              if (allPaths.length > 0) {
                                const isFullySelected = allPaths.every((p) => next.has(p));
                                if (isFullySelected) {
                                  allPaths.forEach((p) => next.delete(p));
                                } else {
                                  allPaths.forEach((p) => next.add(p));
                                }
                                // Keep a clean state: do not store bare top label when subpaths exist
                                next.delete(label);
                              } else {
                                if (next.has(label)) next.delete(label);
                                else next.add(label);
                              }
                              setValue('industries', Array.from(next));
                            }}
                            tabIndex={-1}
                            disableRipple
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary={label}
                          primaryTypographyProps={{ fontWeight: 800 }}
                          secondary={hasAny ? `${counts.selected}${counts.total ? ` / ${counts.total}` : ''} ausgewählt` : undefined}
                          secondaryTypographyProps={{ variant: 'caption' }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>

            <Box sx={{ p: 0 }}>
              <Box sx={{ px: 2, py: 1.25, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 900 }}>
                  {activeTop || 'Kategorie'}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Wähle passende Unterkategorien
                </Typography>
              </Box>
              <List disablePadding>
                {activeTop && subPaths.length === 0 && (
                  <ListItem>
                    <ListItemText
                      primary="Keine Unterkategorien verfügbar"
                      secondary="Diese Kategorie kann nur als Hauptkategorie ausgewählt werden."
                    />
                  </ListItem>
                )}
                {activeTop && subPaths.map((path) => {
                  const parts = path.split(' / ');
                  const leaf = parts[parts.length - 1];
                  const checked = selected.has(path);
                  return (
                    <ListItem key={path} disablePadding>
                      <ListItemButton
                        onClick={() => {
                          const next = new Set(selected);
                          if (next.has(path)) next.delete(path);
                          else next.add(path);
                          // When selecting a subpath, do not keep a bare top-level entry
                          if (activeTop) next.delete(activeTop);
                          setValue('industries', Array.from(next));
                        }}
                        sx={{ py: 1.1, px: 2 }}
                      >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                          <Checkbox edge="start" checked={checked} tabIndex={-1} disableRipple />
                        </ListItemIcon>
                        <ListItemText
                          primary={leaf}
                          primaryTypographyProps={{ fontWeight: 700 }}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            variant="outlined"
            onClick={() => setValue('industries', [])}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
          >
            Zurücksetzen
          </Button>
          <Button
            variant="contained"
            onClick={() => setIndustriesDialogOpen(false)}
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
          >
            Fertig
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const renderJobDetails = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
          Job Details
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Controller
              name="salary"
              control={control}
              rules={{ required: 'Salary is required', min: { value: 0, message: 'Salary must be positive' } }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Salary"
                  type="number"
                  fullWidth
                  error={!!errors.salary}
                  helperText={errors.salary?.message}
                  InputProps={{
                    startAdornment: <InputAdornment position="start">$</InputAdornment>,
                  }}
                />
              )}
            />

            <Controller
              name="salaryType"
              control={control}
              render={({ field }) => (
                <FormControl fullWidth>
                  <InputLabel>Salary Type</InputLabel>
                  <Select {...field} label="Salary Type">
                    {salaryTypes.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              )}
            />
          </Box>

          <Controller
            name="jobType"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>Job Type</InputLabel>
                <Select {...field} label="Job Type">
                  {jobTypes.map((type) => (
                    <MenuItem key={type.value} value={type.value}>
                      {type.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />

          <Controller
            name="experience"
            control={control}
            rules={{ required: 'Experience level is required' }}
            render={({ field }) => (
              <FormControl fullWidth error={!!errors.experience}>
                <InputLabel>Experience Level</InputLabel>
                <Select {...field} label="Experience Level">
                  {experienceLevels.map((level) => (
                    <MenuItem key={level} value={level}>
                      {level}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />

          <Controller
            name="duration"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Duration (Optional)"
                fullWidth
                placeholder="e.g., 3 months, 6 months, Ongoing"
              />
            )}
          />

        </Box>
      </CardContent>
    </Card>
  );

  const renderLocationStep = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
          Job Location
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Controller
            name="hasSpecificLocation"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={field.value}
                    onChange={(e) => {
                      field.onChange(e.target.checked);
                      if (!e.target.checked) {
                        setValue('latitude', undefined);
                        setValue('longitude', undefined);
                        setValue('locationName', '');
                        setSelectedMapLocations([]);
                      }
                    }}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: '#667eea',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: '#667eea',
                      },
                    }}
                  />
                }
                label="This job has a specific location"
                sx={{ mb: 2 }}
              />
            )}
          />

          {watch('hasSpecificLocation') && (
            <>
              <Controller
                name="location"
                control={control}
                rules={{ required: watch('hasSpecificLocation') ? 'Location is required' : false }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Location"
                    placeholder="e.g., New York, NY or Remote"
                    fullWidth
                    error={!!errors.location}
                    helperText={errors.location?.message}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            variant="outlined"
                            startIcon={<LocationIcon />}
                            onClick={() => setMapSearchOpen(true)}
                            sx={{
                              borderColor: 'primary.main',
                              color: 'primary.main',
                              '&:hover': {
                                borderColor: 'primary.dark',
                                backgroundColor: 'action.hover',
                              },
                              textTransform: 'none',
                            }}
                          >
                            Open Map Search
                          </Button>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                )}
              />


              {selectedMapLocations.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    Selected locations:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {selectedMapLocations.map((location, index) => (
                      <Chip
                        key={index}
                        label={location}
                        onDelete={() => {
                          const newLocations = selectedMapLocations.filter((_, i) => i !== index);
                          setSelectedMapLocations(newLocations);
                          setValue('location', newLocations.join(', '));
                        }}
                        sx={{
                          backgroundColor: 'primary.main',
                          color: 'white',
                          '& .MuiChip-deleteIcon': {
                            color: 'white',
                          },
                        }}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              {/* Detailed Location Information */}
              {(watch('latitude') && watch('longitude')) && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="h6" fontWeight="bold" sx={{ mb: 2, color: 'primary.main' }}>
                    📍 Selected Location Details
                  </Typography>
                  
                  <Paper sx={{ p: 2, background: '#f8f9fa', borderRadius: 2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {watch('locationName') && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80, fontWeight: 'bold' }}>
                            📍 Location:
                          </Typography>
                          <Typography variant="body2" color="text.primary">
                            {watch('locationName')}
                          </Typography>
                        </Box>
                      )}
                      
                      {watch('country') && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80, fontWeight: 'bold' }}>
                            🌍 Country:
                          </Typography>
                          <Typography variant="body2" color="text.primary">
                            {watch('country')}
                          </Typography>
                        </Box>
                      )}
                      
                      {watch('state') && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80, fontWeight: 'bold' }}>
                            🏛️ State:
                          </Typography>
                          <Typography variant="body2" color="text.primary">
                            {watch('state')}
                          </Typography>
                        </Box>
                      )}
                      
                      {watch('city') && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80, fontWeight: 'bold' }}>
                            🏙️ City:
                          </Typography>
                          <Typography variant="body2" color="text.primary">
                            {watch('city')}
                          </Typography>
                        </Box>
                      )}
                      
                      {watch('district') && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80, fontWeight: 'bold' }}>
                            🏘️ District:
                          </Typography>
                          <Typography variant="body2" color="text.primary">
                            {watch('district')}
                          </Typography>
                        </Box>
                      )}
                      
                      {(watch('street') || watch('houseNumber')) && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80, fontWeight: 'bold' }}>
                            🛣️ Street:
                          </Typography>
                          <Typography variant="body2" color="text.primary">
                            {watch('street')} {watch('houseNumber')}
                          </Typography>
                        </Box>
                      )}
                      
                      {watch('postalCode') && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80, fontWeight: 'bold' }}>
                            📮 Postal Code:
                          </Typography>
                          <Typography variant="body2" color="text.primary">
                            {watch('postalCode')}
                          </Typography>
                        </Box>
                      )}
                      
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" color="text.secondary" sx={{ minWidth: 80, fontWeight: 'bold' }}>
                          📐 Coordinates:
                        </Typography>
                        <Typography variant="body2" color="text.primary" sx={{ fontFamily: 'monospace' }}>
                          {watch('latitude')?.toFixed(6)}, {watch('longitude')?.toFixed(6)}
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Box>
              )}

              <Alert severity="info" sx={{ borderRadius: 2 }}>
                <Typography variant="body2">
                  <strong>Tip:</strong> You can specify multiple locations if the job can be done from different places. 
                  Use the map to pinpoint exact coordinates for better location matching.
                </Typography>
              </Alert>
            </>
          )}

          <Controller
            name="remote"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={field.value}
                    onChange={field.onChange}
                    sx={{
                      '& .MuiSwitch-switchBase.Mui-checked': {
                        color: 'primary.main',
                      },
                      '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                        backgroundColor: 'primary.main',
                      },
                    }}
                  />
                }
                label="Remote work allowed"
                sx={{ mb: 2 }}
              />
            )}
          />
        </Box>
      </CardContent>
    </Card>
  );

  const renderRequirementsAndBenefits = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
          Requirements & Benefits
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Controller
            name="requiredSkills"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={field.value}
                onChange={(event, newValue) => {
                  field.onChange(newValue);
                }}
                onInputChange={(event, newInputValue, reason) => {
                  // Only handle comma-separated input, not every character
                  if (reason === 'input' && newInputValue.includes(',')) {
                    const parts = newInputValue.split(',');
                    const newSkills = parts
                      .slice(0, -1) // Get all parts except the last one (which might be incomplete)
                      .map(skill => skill.trim())
                      .filter(skill => skill && !field.value.includes(skill));
                    
                    if (newSkills.length > 0) {
                      field.onChange([...field.value, ...newSkills]);
                    }
                    
                    // Clear the input after processing comma
                    if (event && event.target && 'value' in event.target) {
                      (event.target as HTMLInputElement).value = '';
                    }
                  }
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={option}
                      color="primary"
                      variant="filled"
                      size="small"
                      sx={{
                        backgroundColor: '#e3f2fd',
                        color: '#1976d2',
                        fontWeight: 500,
                        '& .MuiChip-deleteIcon': {
                          color: '#1976d2',
                          '&:hover': {
                            color: '#d32f2f',
                          },
                        },
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Required Skills"
                    placeholder="Type skills and press comma to add"
                    helperText="Type skills and press comma to create tags"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        minHeight: '56px',
                        alignItems: 'flex-start',
                        paddingTop: '8px',
                        paddingBottom: '8px',
                      },
                    }}
                  />
                )}
                sx={{
                  '& .MuiAutocomplete-inputRoot': {
                    flexWrap: 'wrap',
                  },
                }}
              />
            )}
          />

          <Controller
            name="jobRequirements"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={field.value}
                onChange={(event, newValue) => {
                  field.onChange(newValue);
                }}
                onInputChange={(event, newInputValue, reason) => {
                  // Only handle comma-separated input, not every character
                  if (reason === 'input' && newInputValue.includes(',')) {
                    const parts = newInputValue.split(',');
                    const newRequirements = parts
                      .slice(0, -1) // Get all parts except the last one (which might be incomplete)
                      .map(req => req.trim())
                      .filter(req => req && !field.value.includes(req));
                    
                    if (newRequirements.length > 0) {
                      field.onChange([...field.value, ...newRequirements]);
                    }
                    
                    // Clear the input after processing comma
                    if (event && event.target && 'value' in event.target) {
                      (event.target as HTMLInputElement).value = '';
                    }
                  }
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={option}
                      color="secondary"
                      variant="filled"
                      size="small"
                      sx={{
                        backgroundColor: '#f3e5f5',
                        color: '#7b1fa2',
                        fontWeight: 500,
                        '& .MuiChip-deleteIcon': {
                          color: '#7b1fa2',
                          '&:hover': {
                            color: '#d32f2f',
                          },
                        },
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Job Requirements"
                    placeholder="Type requirements and press comma to add"
                    helperText="Type requirements and press comma to create tags"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        minHeight: '56px',
                        alignItems: 'flex-start',
                        paddingTop: '8px',
                        paddingBottom: '8px',
                      },
                    }}
                  />
                )}
                sx={{
                  '& .MuiAutocomplete-inputRoot': {
                    flexWrap: 'wrap',
                  },
                }}
              />
            )}
          />

          <Controller
            name="benefitsPerks"
            control={control}
            render={({ field }) => (
              <Autocomplete
                multiple
                freeSolo
                options={[]}
                value={field.value}
                onChange={(event, newValue) => {
                  field.onChange(newValue);
                }}
                onInputChange={(event, newInputValue, reason) => {
                  // Only handle comma-separated input, not every character
                  if (reason === 'input' && newInputValue.includes(',')) {
                    const parts = newInputValue.split(',');
                    const newBenefits = parts
                      .slice(0, -1) // Get all parts except the last one (which might be incomplete)
                      .map(benefit => benefit.trim())
                      .filter(benefit => benefit && !field.value.includes(benefit));
                    
                    if (newBenefits.length > 0) {
                      field.onChange([...field.value, ...newBenefits]);
                    }
                    
                    // Clear the input after processing comma
                    if (event && event.target && 'value' in event.target) {
                      (event.target as HTMLInputElement).value = '';
                    }
                  }
                }}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={option}
                      label={option}
                      color="success"
                      variant="filled"
                      size="small"
                      sx={{
                        backgroundColor: '#e8f5e8',
                        color: '#2e7d32',
                        fontWeight: 500,
                        '& .MuiChip-deleteIcon': {
                          color: '#2e7d32',
                          '&:hover': {
                            color: '#d32f2f',
                          },
                        },
                      }}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Benefits & Perks"
                    placeholder="Type benefits and press comma to add"
                    helperText="Type benefits and press comma to create tags"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        minHeight: '56px',
                        alignItems: 'flex-start',
                        paddingTop: '8px',
                        paddingBottom: '8px',
                      },
                    }}
                  />
                )}
                sx={{
                  '& .MuiAutocomplete-inputRoot': {
                    flexWrap: 'wrap',
                  },
                }}
              />
            )}
          />

          <Controller
            name="contactEmail"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label="Contact Email"
                fullWidth
                type="email"
                placeholder="applications@company.com"
              />
            )}
          />

          <Box sx={{ display: 'flex', gap: 2 }}>
            <Controller
              name="applicationDeadline"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Application Deadline"
                  fullWidth
                  type="date"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />

            <Controller
              name="startDate"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Expected Start Date"
                  fullWidth
                  type="date"
                  InputLabelProps={{ shrink: true }}
                />
              )}
            />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const renderReviewAndPublish = () => (
    <Card sx={{ mb: 3 }}>
      <CardContent sx={{ p: 3 }}>
        <Typography variant="h6" fontWeight="bold" gutterBottom sx={{ mb: 3 }}>
          Review & Publish
        </Typography>
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {(() => {
            const selectedIds = Array.isArray(watch('promotionAddons')) ? (watch('promotionAddons') as any[]) : [];
            const currency = (resolvedPromotionOptions?.currency || 'EUR').trim() || 'EUR';
            const boostOptionById = new Map(
              (resolvedPromotionOptions?.options || []).map((o) => [String(o.id || ''), o] as const)
            );
            const selectedBoost = selectedIds
              .filter((id) => !!String(id || '').trim())
              .map((id) => {
                const opt = boostOptionById.get(String(id));
                return {
                  id: String(id),
                  title: opt?.title,
                  description: opt?.description,
                  price: typeof opt?.price === 'number' ? opt?.price : 0,
                };
              });
            const total = selectedBoost.reduce((sum, it) => sum + (Number(it.price) || 0), 0);

            return (
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  borderWidth: 2,
                  borderColor: total > 0 ? 'success.light' : 'divider',
                  background: total > 0
                    ? 'linear-gradient(135deg, rgba(16,185,129,0.06) 0%, rgba(59,130,246,0.06) 100%)'
                    : 'transparent',
                }}
              >
                <CardContent sx={{ p: 2.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1.5 }}>
                    <Box>
                      <Typography fontWeight={900}>Boost & Visibility</Typography>
                      <Typography variant="body2" color="text.secondary">
                        Optional add-ons to increase reach.
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: 'right' }}>
                      <Typography variant="body2" color="text.secondary">Total</Typography>
                      <Typography fontWeight={900} sx={{ color: total > 0 ? 'success.main' : 'text.primary' }}>
                        {total.toFixed(2)} {currency}
                      </Typography>
                    </Box>
                  </Box>

                  {selectedBoost.length === 0 ? (
                    <Alert severity="info">
                      No boost options selected. You can publish normally.
                    </Alert>
                  ) : (
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                      {selectedBoost.map((it) => (
                        <Box
                          key={it.id}
                          sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, p: 1.25, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.65)' }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography fontWeight={800} sx={{ lineHeight: 1.2 }}>
                              {it.title || it.id}
                            </Typography>
                            {!!it.description && (
                              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                                {it.description}
                              </Typography>
                            )}
                          </Box>
                          <Typography fontWeight={900} sx={{ whiteSpace: 'nowrap' }}>
                            {(Number(it.price) || 0).toFixed(2)} {currency}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  )}
                </CardContent>
              </Card>
            );
          })()}

          <Controller
            name="isActive"
            control={control}
            render={({ field }) => (
              <FormControlLabel
                control={<Switch {...field} checked={field.value} />}
                label="Publish job posting immediately"
              />
            )}
          />
          
          <Alert severity="info">
            Review your job posting carefully before publishing. You can edit it later if needed.
          </Alert>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.50' }}>
      {/* Header */}
      <Box
        sx={{
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
          color: 'white',
          py: 4,
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
            <IconButton
              onClick={() => {
                // Check if user came from company dashboard and preserve context
                const companyParam = searchParams.get('company');
                const nameParam = searchParams.get('name');
                if (companyParam && nameParam) {
                  navigate(`/dashboard?company=${companyParam}&name=${nameParam}`);
                } else {
                  navigate('/dashboard');
                }
              }}
              sx={{ color: 'white' }}
            >
              <BackIcon />
            </IconButton>
            <Box>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Upload New Job
              </Typography>
              <Typography variant="h6" sx={{ opacity: 0.9 }}>
                Post job opportunities and find talented freelancers
              </Typography>
            </Box>
          </Box>

          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: 4 }}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          {renderIndustriesDialog()}
          {activeStep === 0 && renderBasicInformation()}
          {activeStep === 1 && renderJobDetails()}
          {activeStep === 2 && renderLocationStep()}
          {activeStep === 3 && renderRequirementsAndBenefits()}
          {activeStep === 4 && renderBoostAndVisibility()}
          {activeStep === 5 && renderReviewAndPublish()}

          <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
            <Button
              type="button"
              disabled={activeStep === 0}
              onClick={handleBack}
              sx={{ mr: 1 }}
            >
              Back
            </Button>
            <Box>
              {activeStep === steps.length - 1 ? (
                <Button
                  type="button"
                  variant="contained"
                  onClick={handleSubmit(handlePublishClick, handlePublishInvalid)}
                  disabled={loading || paypalProcessing}
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                    },
                  }}
                >
                  {loading || paypalProcessing ? <CircularProgress size={24} /> : 'Publish Job'}
                </Button>
              ) : (
                <Button
                  type="button"
                  variant="contained"
                  onClick={handleNext}
                  sx={{
                    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    px: 4,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    '&:hover': {
                      background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
                    },
                  }}
                >
                  Next
                </Button>
              )}
            </Box>
          </Box>
        </form>
      </Container>

      {/* Success Modal */}
      <Dialog
        open={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: 'center', pt: 4 }}>
          <CheckCircleIcon sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
          <Typography variant="h5" fontWeight="bold">
            Job Posted Successfully!
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
          <Typography variant="body1" color="text.secondary">
            Your job posting has been published and is now visible to potential candidates.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 4 }}>
          <Button
            onClick={() => {
              setShowSuccessModal(false);
              // Check if user came from company dashboard and preserve context
              const companyParam = searchParams.get('company');
              const nameParam = searchParams.get('name');
              if (companyParam && nameParam) {
                navigate(`/dashboard?company=${companyParam}&name=${nameParam}`);
              } else {
                navigate('/dashboard');
              }
            }}
            variant="contained"
            sx={{
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
              px: 4,
              py: 1.5,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 600,
            }}
          >
            View Dashboard
          </Button>
        </DialogActions>
      </Dialog>

      {/* Map Search Modal */}
      <MapSearchNew
        open={mapSearchOpen}
        onClose={() => setMapSearchOpen(false)}
        onLocationSelect={(location) => {
          const newLocations = [...selectedMapLocations, location.name];
          setSelectedMapLocations(newLocations);
          setValue('location', newLocations.join(', '));
          setValue('locationName', location.name);
          setValue('latitude', location.lat);
          setValue('longitude', location.lng);
          // Store additional detailed information for display
          setValue('country', location.country || '');
          setValue('state', location.state || '');
          setValue('city', location.city || '');
          setValue('district', location.district || '');
          setValue('street', location.street || '');
          setValue('houseNumber', location.houseNumber || '');
          setValue('postalCode', location.postalCode || '');
        }}
        selectedLocations={selectedMapLocations}
        initialSearchQuery={watch('location')}
      />
    </Box>
  );
};

export default JobUploadPage;
